/**
 * Group overview (web) — the informal-group (G2) aggregate dashboard, open to
 * the lead AND every member.
 *
 * Web twin of apps/mobile/app/groups/overview.tsx. The groups screen shipped
 * coordination (roster / invite / leave / dissolve) but gave members no view
 * of the group's combined output. This fills that gap from EXISTING reads — no
 * new endpoint:
 *
 *   - GET /groups/mine   → group name + member count + isLead
 *   - GET /batches       → the group's shared harvests (server-scoped to the
 *                          caller's group); summed/bucketed client-side
 *   - GET /contributions → per-batch rows, aggregated by member
 *
 * MEMBER-READABLE: GET /batches + /contributions were lead-only, but the backend
 * now group-scopes them read-only for plain members too (full transparency). So
 * the gate here is membership, not leadership: any in-group user gets the full
 * overview, with their own breakdown row highlighted; a non-member who
 * deep-links here gets a gentle "not in a group" card. Tap-through to the
 * coop-admin batch detail stays lead-only (members read, they don't manage).
 *
 * Online-only (plain react-query). Mirrors _app.insights.tsx (DetailRow +
 * RowGroup + Card). Contracts verified against the generated client models
 * batchDto.ts / contributionDto.ts / groupDetailDto.ts.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  ListItem,
  Stack,
  Text,
} from '@fse/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import { batchesControllerList } from '../api/generated/batches/batches';
import { contributionsControllerList } from '../api/generated/contributions/contributions';
import { groupsControllerGetMine } from '../api/generated/groups/groups';
import type { BatchDtoStatus, GroupDetailDto } from '../api/generated/models';

const BATCH_STATUSES: BatchDtoStatus[] = ['open', 'locked', 'sold', 'settled', 'cancelled'];

/** Cap on batches scanned for the per-member breakdown (one /contributions
 * call each) — a runaway guard; a pilot group has a handful of harvests. */
const BREAKDOWN_BATCH_CAP = 50;

const GROUPS_MINE_KEY = ['groups-mine'] as const;

/** A label/value row rendered as a kit ListItem (mirrors _app.insights.tsx). */
function DetailRow({
  label,
  value,
  noDivider = false,
}: {
  label: string;
  value: ReactNode;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      title={
        <Text variant="body-medium" color="text.secondary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text variant="body-medium" color="text.primary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

function RowGroup({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {children}
    </Box>
  );
}

export function GroupOverviewScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Group affiliation. 404 (not in a group) resolves to null rather than error.
  const groupQuery = useQuery<GroupDetailDto | null>({
    queryKey: GROUPS_MINE_KEY,
    queryFn: async ({ signal }) => {
      try {
        return await groupsControllerGetMine(signal);
      } catch (err) {
        if (err instanceof FseApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  const group = groupQuery.data ?? null;
  const isLead = group?.isLead ?? false;
  // Membership, not leadership, gates the overview now: a plain member reads the
  // same group-scoped aggregates (backend relaxed /batches + /contributions to
  // be member-readable). `inGroup` drives the queries and the card switch.
  const inGroup = group !== null;
  // The caller's own user id, to highlight their row in the member breakdown.
  const selfUserId = readAccessTokenClaims()?.sub ?? null;

  // Shared harvests — group-scoped, readable by any member.
  const batchesQuery = useQuery({
    queryKey: ['group-batches'],
    queryFn: async ({ signal }) => {
      const res = await batchesControllerList({ limit: 100, offset: 0 }, signal);
      return res.data;
    },
    enabled: inGroup,
  });

  const batches = batchesQuery.data;
  const batchIds = useMemo(() => (batches ?? []).map((b) => b.id), [batches]);

  const breakdownQuery = useQuery({
    queryKey: ['group-member-breakdown', batchIds],
    enabled: inGroup && batchIds.length > 0,
    queryFn: async () => {
      const scanned = batchIds.slice(0, BREAKDOWN_BATCH_CAP);
      const lists = await Promise.all(
        scanned.map((id) => contributionsControllerList({ batchId: id, limit: 200, offset: 0 })),
      );
      const byMember = new Map<string, { userId: string; displayName: string; totalKg: number; count: number }>();
      for (const list of lists) {
        for (const c of list.data) {
          const existing = byMember.get(c.farmerUserId) ?? {
            userId: c.farmerUserId,
            displayName: c.farmerDisplayName || c.farmerUserId,
            totalKg: 0,
            count: 0,
          };
          existing.totalKg += Number(c.qtyKg) || 0;
          existing.count += 1;
          if (c.farmerDisplayName) existing.displayName = c.farmerDisplayName;
          byMember.set(c.farmerUserId, existing);
        }
      }
      return Array.from(byMember.values()).sort((a, b) => b.totalKg - a.totalKg);
    },
  });

  const production = useMemo(() => {
    if (!batches) return null;
    const byStatus: Record<BatchDtoStatus, number> = {
      open: 0,
      locked: 0,
      sold: 0,
      settled: 0,
      cancelled: 0,
    };
    let totalKg = 0;
    let totalContributions = 0;
    for (const b of batches) {
      byStatus[b.status] = (byStatus[b.status] ?? 0) + 1;
      totalKg += Number(b.totalKg) || 0;
      totalContributions += b.contributionCount;
    }
    const recent = [...batches]
      .sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt))
      .slice(0, 5);
    return { batchCount: batches.length, byStatus, totalKg, totalContributions, recent };
  }, [batches]);

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('groupOverview.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('groupOverview.subtitle')}
          </Text>
        </Box>
      </Box>

      {groupQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : groupQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => groupQuery.refetch()}>
              {t('groupOverview.retry')}
            </Button>
          }
        >
          {t('groupOverview.error')}
        </Alert>
      ) : !inGroup ? (
        <Card>
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('groupOverview.notInGroup')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Group header — lead vs member framing. */}
          <Card>
            <CardContent>
              <Stack spacing={0.5} sx={{ alignItems: 'center', textAlign: 'center', py: 1 }}>
                <Text variant="label-medium" color="text.secondary">
                  {isLead
                    ? t('groupOverview.youLead')
                    : t('groupOverview.memberOf', { name: group?.name ?? '' })}
                </Text>
                <Text variant="headline-small" color="primary.main">
                  {group?.name ?? ''}
                </Text>
                <Text variant="body-medium" color="text.secondary">
                  {t('groupOverview.memberCount', { count: group?.memberCount ?? 0 })}
                </Text>
              </Stack>
            </CardContent>
          </Card>

          {batchesQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : batchesQuery.isError ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => batchesQuery.refetch()}>
                  {t('groupOverview.retry')}
                </Button>
              }
            >
              {t('groupOverview.error')}
            </Alert>
          ) : !production || production.batchCount === 0 ? (
            <Card>
              <CardContent>
                <Stack spacing={1}>
                  <Text variant="title-medium" as="h3">
                    {t('groupOverview.empty.title')}
                  </Text>
                  <Text variant="body-medium" color="text.secondary">
                    {t('groupOverview.empty.body')}
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Production headline */}
              <Card>
                <CardHeader title={t('groupOverview.production.title')} />
                <CardContent>
                  <RowGroup>
                    <DetailRow
                      label={t('groupOverview.production.batches')}
                      value={String(production.batchCount)}
                    />
                    <DetailRow
                      label={t('groupOverview.production.totalKg')}
                      value={t('groupOverview.kgValue', { kg: production.totalKg.toFixed(2) })}
                    />
                    <DetailRow
                      label={t('groupOverview.production.contributions')}
                      value={String(production.totalContributions)}
                      noDivider
                    />
                  </RowGroup>
                </CardContent>
              </Card>

              {/* Harvests by status */}
              <Card>
                <CardHeader title={t('groupOverview.byStatus.title')} />
                <CardContent>
                  <RowGroup>
                    {BATCH_STATUSES.map((status, idx) => (
                      <DetailRow
                        key={status}
                        label={t(`batches.status.${status}`)}
                        value={String(production.byStatus[status])}
                        noDivider={idx === BATCH_STATUSES.length - 1}
                      />
                    ))}
                  </RowGroup>
                </CardContent>
              </Card>

              {/* Member contributions */}
              <Card>
                <CardHeader title={t('groupOverview.members.title')} />
                <CardContent>
                  {breakdownQuery.isPending ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : breakdownQuery.isError ? (
                    <Text variant="body-medium" color="text.secondary">
                      {t('groupOverview.members.error')}
                    </Text>
                  ) : !breakdownQuery.data || breakdownQuery.data.length === 0 ? (
                    <Text variant="body-medium" color="text.secondary">
                      {t('groupOverview.members.empty')}
                    </Text>
                  ) : (
                    <RowGroup>
                      {breakdownQuery.data.map((m, idx) => (
                        <ListItem
                          key={m.userId}
                          leadingIcon="person"
                          title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Text variant="body-medium" color="text.primary">
                                <Box
                                  component="span"
                                  sx={{ fontWeight: m.userId === selfUserId ? 700 : 400 }}
                                >
                                  {m.displayName}
                                </Box>
                              </Text>
                              {m.userId === selfUserId ? (
                                <Chip kind="assist" color="success" label={t('groupOverview.you')} />
                              ) : null}
                            </Box>
                          }
                          supportingText={
                            <Text variant="body-small" color="text.secondary">
                              {t('groupOverview.members.count', { count: m.count })}
                            </Text>
                          }
                          trailing={
                            <Text variant="body-medium" color="text.primary">
                              <Box component="span" sx={{ fontWeight: 600 }}>
                                {t('groupOverview.kgValue', { kg: m.totalKg.toFixed(2) })}
                              </Box>
                            </Text>
                          }
                          noDivider={idx === breakdownQuery.data.length - 1}
                        />
                      ))}
                    </RowGroup>
                  )}
                </CardContent>
              </Card>

              {/* Recent shared harvests — the lead taps through to the
                  coop-admin batch detail; a plain member reads only (no nav to
                  a screen they can't manage), so the row is non-clickable. */}
              <Card>
                <CardHeader title={t('groupOverview.recent.title')} />
                <CardContent>
                  <RowGroup>
                    {production.recent.map((b, idx) => (
                      <RecentBatchRow
                        key={b.id}
                        label={t('groupOverview.recent.batchTitle', { id: b.id.slice(0, 8) })}
                        kg={t('groupOverview.kgValue', { kg: b.totalKg })}
                        statusLabel={t(`batches.status.${b.status}`)}
                        onClick={
                          isLead
                            ? () => navigate({ to: '/batches/$id', params: { id: b.id } })
                            : undefined
                        }
                        noDivider={idx === production.recent.length - 1}
                      />
                    ))}
                  </RowGroup>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

interface RecentBatchRowProps {
  label: string;
  kg: string;
  statusLabel: string;
  /** Tap-through to the batch detail — lead only. Undefined renders the row
   *  non-clickable (a member reads, but can't open the coop-admin screen). */
  onClick?: () => void;
  noDivider: boolean;
}

function RecentBatchRow({ label, kg, statusLabel, onClick, noDivider }: RecentBatchRowProps) {
  return (
    <ListItem
      onClick={onClick}
      title={
        <Text variant="body-medium" color="text.primary">
          {label}
        </Text>
      }
      supportingText={
        <Text variant="body-small" color="text.secondary">
          {kg}
        </Text>
      }
      trailing={<Chip kind="assist" color="tertiary" label={statusLabel} />}
      noDivider={noDivider}
    />
  );
}

export default GroupOverviewScreen;
